import { useCallback, useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { SimpleAvatar } from '@/components/shared/SimpleAvatar';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFamilyMembers } from '@/hooks/use-family-members';

const AVATAR_SIZE = 48;

/**
 * FamilyAvatarsRow - horizontal strip of family member avatars on Home.
 * Tapping an avatar opens the Family screen; the trailing "+" adds a member.
 * Surfaces the otherwise-hidden Family tab (pain point: partner coordination).
 */
export function FamilyAvatarsRow() {
  const router = useRouter();
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { members, isLoading } = useFamilyMembers();

  // "Me" first, then the rest in store order.
  const orderedMembers = useMemo(
    () => [...members].sort((a, b) => Number(b.isMe) - Number(a.isMe)),
    [members]
  );

  const handleOpenFamily = useCallback(() => {
    router.navigate({ pathname: '/(tabs)/family', params: { from: 'home' } });
  }, [router]);

  const handleAddMember = useCallback(() => {
    router.push('/family-member/create');
  }, [router]);

  if (isLoading && members.length === 0) {
    return null;
  }

  const hasAdditionalMembers = members.some((member) => !member.isMe);

  return (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: palette.primary, fontFamily: getBodyFont() }]}>
        Family
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.row}>
        {orderedMembers.map((member) => (
          <Pressable
            key={member.id}
            onPress={handleOpenFamily}
            style={({ pressed }) => [styles.item, { opacity: pressed ? 0.6 : 1 }]}
            accessibilityRole="button"
            accessibilityLabel={`Open family screen — ${member.name}`}
          >
            <SimpleAvatar
              name={member.name}
              color={member.color}
              size={AVATAR_SIZE}
              imageUri={member.profilePicture}
            />
            <Text
              style={[styles.itemLabel, { color: palette.textMuted, fontFamily: getBodyFont() }]}
              numberOfLines={1}
            >
              {member.isMe ? 'Me' : member.name}
            </Text>
          </Pressable>
        ))}

        {!hasAdditionalMembers && (
          <Pressable
            onPress={handleAddMember}
            style={({ pressed }) => [styles.item, { opacity: pressed ? 0.6 : 1 }]}
            accessibilityRole="button"
            accessibilityLabel="Add family member"
          >
            <View style={[styles.addCircle, { borderColor: palette.border }]}>
              <Ionicons name="add" size={22} color={palette.textMuted} />
            </View>
            <Text style={[styles.itemLabel, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
              Add
            </Text>
          </Pressable>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    gap: Spacing.sm,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  row: {
    gap: Spacing.lg,
    paddingVertical: Spacing.xs,
  },
  item: {
    alignItems: 'center',
    gap: Spacing.xs,
    width: AVATAR_SIZE + 16,
  },
  itemLabel: {
    fontSize: 11,
    maxWidth: AVATAR_SIZE + 16,
  },
  addCircle: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
